/*!
 * video-upgrade.js
 * Automatically upgrades any <iframe> pointing at a direct video URL into a
 * "poster image + muted autoplay" player.
 *
 * Usage (Shopify theme):
 *   1. Upload this file to Theme -> Edit code -> assets/
 *   2. Then keep pasting plain iframes into your blog posts as usual:
 *        <iframe data-poster="" src="https://cdn.shopify.com/videos/xxx.mp4" width="100%" height="420"></iframe>
 *
 * Graceful degradation: if the JS never runs, the original iframe stays and the
 * browser's built-in player still plays the video.
 *
 * Optional configuration (define it before this script is loaded):
 *   <script>
 *     window.VideoUpgradeConfig = {
 *       autoplay: true,      // muted autoplay once the player enters the viewport
 *       loop: true,          // loop playback
 *       sound: true,         // show a "click to unmute" button
 *       controls: true,      // show the playback control bar
 *       ratio: '',           // force an aspect ratio such as '16/9'; empty keeps the original iframe size
 *       probePoster: true,   // when no poster is given, try a same-name image in the same directory
 *       posterSuffix: '.jpg' // suffix of that same-name poster image
 *     };
 *   </script>
 */
(function () {
  'use strict';

  var CFG = window.VideoUpgradeConfig || {};
  var OPTS = {
    autoplay: CFG.autoplay !== false,
    loop: CFG.loop !== false,
    sound: CFG.sound !== false,
    controls: CFG.controls !== false,
    ratio: CFG.ratio || '',
    probePoster: CFG.probePoster !== false,
    posterSuffix: CFG.posterSuffix || '.jpg',
    debug: !!CFG.debug
  };

  // Only these video extensions are accepted, so YouTube / Vimeo embeds are left alone
  var VIDEO_EXT = /\.(mp4|m4v|webm|ogv|mov)(?:$|[?#])/i;

  function log() {
    if (OPTS.debug && window.console) {
      console.log.apply(console, ['[video-upgrade]'].concat([].slice.call(arguments)));
    }
  }

  /* ---------- Check whether an iframe points at a direct video URL ---------- */
  function videoUrlOf(iframe) {
    var raw = iframe.getAttribute('src') || '';
    if (!raw) return '';
    var u;
    try {
      u = new URL(raw, location.href);
    } catch (e) {
      return '';
    }
    // Only http/https is handled, blocking javascript: / data: and friends
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    if (!VIDEO_EXT.test(u.pathname)) return '';
    return u.href;
  }

  /* ---------- Poster: data-poster first, then a same-name image in the same directory ---------- */

  function posterCandidates(videoUrl) {
    var u = new URL(videoUrl, location.href);
    var base = u.pathname.replace(/\.(mp4|m4v|webm|ogv|mov)$/i, '');
    var dir = u.origin + base;
    return [
      dir + OPTS.posterSuffix,
      dir + '.jpg',
      dir + '.png',
      dir + '-poster' + OPTS.posterSuffix
    ].filter(function (v, i, a) { return a.indexOf(v) === i; });
  }

  function probeImage(urls, done) {
    var i = 0;
    (function next() {
      if (i >= urls.length) { done(''); return; }
      var img = new Image();
      img.onload = function () { done(urls[i]); };
      img.onerror = function () { i += 1; next(); };
      img.src = urls[i];
    })();
  }

  /* ---------- Styles and logic of the page inside the iframe ---------- */

  var INNER_CSS =
    'html,body{margin:0;padding:0;height:100%;background:#000;overflow:hidden;' +
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif}' +
    '.vu{position:relative;width:100%;height:100%;background:#000}' +
    '.vu video{width:100%;height:100%;object-fit:contain;display:block;background:#000}' +
    '.vu .cv{position:absolute;inset:0;background:#000 center/cover no-repeat;' +
    'display:flex;align-items:center;justify-content:center;cursor:pointer;' +
    'transition:opacity .45s ease}' +
    '.vu .cv.hide{opacity:0;pointer-events:none}' +
    '.vu .pb{width:68px;height:68px;border-radius:50%;background:rgba(0,0,0,.55);' +
    'border:2px solid rgba(255,255,255,.85);display:flex;align-items:center;justify-content:center;' +
    'transition:transform .2s ease}' +
    '.vu .cv:hover .pb{transform:scale(1.08)}' +
    '.vu .pb::after{content:"";border-style:solid;border-width:13px 0 13px 21px;' +
    'border-color:transparent transparent transparent #fff;margin-left:5px}' +
    '.vu .mu{position:absolute;right:12px;bottom:12px;border:0;border-radius:100px;' +
    'background:rgba(0,0,0,.66);color:#fff;font-size:12px;padding:7px 13px;cursor:pointer;' +
    '-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px)}' +
    '.vu .mu:hover{background:rgba(0,0,0,.85)}';

  // Player logic for the inner document; __SRC__ / __SETPOSTER__ are injected by buildDoc.
  // Note: autoplay scheduling belongs to the parent page -- an IntersectionObserver inside
  // the iframe would watch the iframe's own viewport and cannot sense the top-level scroll
  // position, so the inner document only handles UI and listens for commands.
  var INNER_JS =
    '(function(){' +
    'var v=document.getElementById("v"),c=document.getElementById("c");' +
    'var started=false;' +
    'v.src=__SRC__;' +
    'v.muted=__MUTED__;' +
    'function play(){var p=v.play();if(p&&p.catch)p.catch(function(){});}' +
    '__SETPOSTER__' +
    'c.addEventListener("click",function(){v.muted=false;play();started=true;});' +
    'v.addEventListener("playing",function(){' +
    'started=true;c.classList.add("hide");});' +
    'v.addEventListener("error",function(){c.classList.add("hide");});' +
    'window.addEventListener("message",function(e){' +
    'var d=e.data||{};' +
    'if(d.vu==="play"){if(!started)setTimeout(function(){play();},3000)}' +
    'else if(d.vu==="pause"){if(!v.paused)v.pause();}' +
    '});' +
    '})();';

  function buildDoc(videoUrl, posterUrl) {
    var js = INNER_JS
      .replace('__SRC__', JSON.stringify(videoUrl))
      .replace('__MUTED__', OPTS.autoplay ? 'true' : 'false')
      .replace('__SOUND__', OPTS.sound ? 'true' : 'false');

    if (posterUrl) {
      js = js.replace('__SETPOSTER__',
        'v.poster=' + JSON.stringify(posterUrl) + ';' +
        'c.style.backgroundImage="url("+' + JSON.stringify(posterUrl) + '+")";');
    } else {
      // Without a poster, hide the overlay so a black block does not swallow clicks
      js = js.replace('__SETPOSTER__', 'c.style.display="none";');
    }

    return '<!doctype html><html><head><meta charset="utf-8">' +
      '<meta name="viewport" content="width=device-width,initial-scale=1">' +
      '<style>' + INNER_CSS + '</style></head><body>' +
      '<div class="vu">' +
      '<video id="v" playsinline preload="metadata"' +
      // Force the control bar when there is no poster, otherwise the user just sees a black box
      (OPTS.controls || !posterUrl ? ' controls' : '') +
      (OPTS.loop ? ' loop' : '') + '></video>' +
      '<div class="cv" id="c"><span class="pb"></span></div>' +
      '</div>' +
      '<script>' + js + '<\/script>' +
      '</body></html>';
  }

  /* ---------- Viewport scheduling: must be done by the parent page ---------- */

  // An IntersectionObserver inside the iframe works on the iframe's own viewport and cannot
  // tell whether the top-level page has scrolled to it, so we observe the iframe element from outside.
  function watchVisibility(shell) {
    if (!OPTS.autoplay || !('IntersectionObserver' in window)) return;

    function start() {
      function tell(msg) {
        try {
          shell.contentWindow.postMessage({ vu: msg }, '*');
        } catch (e) { /* In rare cases contentWindow is unreachable; ignore */ }
      }
      new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          tell(entries[i].isIntersecting ? 'play' : 'pause');
        }
      }, { threshold: 0.5 }).observe(shell);
      log('watching visibility');
    }

    // Wait until the inner message listener is registered before sending commands
    var ready = false;
    try {
      ready = shell.contentDocument && shell.contentDocument.readyState === 'complete';
    } catch (e) { /* Inner document unreachable; fall back to the load event */ }
    if (ready) start();
    else shell.addEventListener('load', start);
  }

  /* ---------- Upgrade: swap in an "empty shell" iframe whose content comes from srcdoc ---------- */

  function upgrade(iframe, videoUrl, posterUrl) {
    var shell = document.createElement('iframe');

    shell.srcdoc = buildDoc(videoUrl, posterUrl);
    // Autoplay inside a cross-origin iframe needs this permission
    shell.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
    shell.setAttribute('allowfullscreen', '');
    shell.setAttribute('loading', 'lazy');
    shell.setAttribute('title', iframe.getAttribute('title') || 'Embedded Video');
    shell.dataset.vuUpgraded = '1';

    // Inherit the original iframe's appearance to avoid layout shift
    if (iframe.className) shell.className = iframe.className;
    if (iframe.getAttribute('width')) shell.setAttribute('width', iframe.getAttribute('width'));
    if (iframe.getAttribute('height')) shell.setAttribute('height', iframe.getAttribute('height'));
    if (iframe.style.cssText) shell.style.cssText = iframe.style.cssText;

    // Size fallback: when the original iframe has no height, apply a ratio or it collapses to 150px
    var hasHeight = iframe.getAttribute('height') ||
      (iframe.style.height && iframe.style.height !== 'auto') ||
      (iframe.style.aspectRatio);
    if (!hasHeight) {
      shell.style.width = iframe.style.width || '100%';
      shell.style.aspectRatio = OPTS.ratio || '16/9';
      shell.style.height = 'auto';
      shell.style.border = '0';
      shell.style.display = 'block';
    } else if (!iframe.style.border) {
      shell.style.border = '0';
    }

    iframe.replaceWith(shell);
    watchVisibility(shell);
    log('upgraded', videoUrl, posterUrl ? '(with poster)' : '(no poster)');
  }

  /* ---------- Scanning and initialization ---------- */

  function handle(iframe) {
    if (iframe.dataset.vuUpgraded) return;
    // Add a data-vu-skip attribute to leave a particular iframe untouched
    if (iframe.hasAttribute('data-vu-skip')) return;

    var videoUrl = videoUrlOf(iframe);
    if (!videoUrl) return;

    // Mark it first so the MutationObserver does not re-enter on the same node
    iframe.dataset.vuUpgraded = 'pending';

    var poster = iframe.getAttribute('data-poster') ||
      iframe.getAttribute('poster') || '';

    if (poster || !OPTS.probePoster) {
      upgrade(iframe, videoUrl, poster);
      return;
    }

    // No poster given: probe for a same-name image in the same directory, costing a few 404s at most
    probeImage(posterCandidates(videoUrl), function (found) {
      // It may already have been handled by other logic while probing
      if (!iframe.isConnected || iframe.dataset.vuUpgraded !== 'pending') return;
      upgrade(iframe, videoUrl, found);
    });
  }

  function scan(root) {
    var nodes = (root || document).querySelectorAll('iframe[src][data-upgrade]');
    for (var i = 0; i < nodes.length; i += 1) handle(nodes[i]);
  }

  function init() {
    scan(document);

    // Dynamically inserted iframes (AJAX-loaded comments, theme editor re-renders, etc.) need handling too
    if (window.MutationObserver) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i += 1) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j += 1) {
            var n = added[j];
            if (!n || n.nodeType !== 1) continue;
            if (n.tagName === 'IFRAME') handle(n);
            else if (n.querySelectorAll) scan(n);
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }

    // Shopify theme editor: rescan after a section re-renders
    document.addEventListener('shopify:section:load', function (ev) {
      scan(ev.target);
    });
  }

  if (document.readyState === 'loading') {
    // Run early when placed in <head>; iframes inserted during parsing are caught by the MutationObserver
    init();
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    init();
  }

  // Expose internals in debug mode for console debugging / automated verification
  if (OPTS.debug) {
    window.VideoUpgrade = {
      buildDoc: buildDoc,
      upgrade: upgrade,
      scan: scan,
      handle: handle,
      options: OPTS
    };
  }
})();
